import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameController, Coin, Sparkle, ArrowRight, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Games() {
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null); // {session_id, questions}
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (!user || user === false) { toast.error("Please log in to play"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get("/games/quiz/new");
      setQuiz(data);
      setAnswers(new Array(data.questions.length).fill(-1));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (answers.some((a) => a === -1)) { toast.error("Answer all questions first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/games/quiz/submit", { session_id: quiz.session_id, answers });
      setResult(data);
      toast.success(`You earned ${data.reward} credits!`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!user || user === false) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <GameController size={40} className="mx-auto text-[#2C4A3B]" />
        <h1 className="font-heading text-4xl mt-4">Play & Earn is for members</h1>
        <p className="text-[#5C564D] mt-3">Log in to play the daily quiz and earn credits usable in the marketplace.</p>
        <Link to="/login" data-testid="games-login-cta" className="samaaj-btn-primary mt-6 inline-block">Log in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D49A36]">Play & Earn</div>
      <h1 className="font-heading text-5xl mt-2 tracking-tight">Answer, earn, <em className="text-[#D49A36] not-italic">spend</em>.</h1>
      <p className="text-[#5C564D] mt-2">10 credits for every correct answer. Redeem in the marketplace.</p>

      {!quiz && !result && (
        <div className="samaaj-card p-8 mt-8" data-testid="quiz-intro">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "#D49A36" }}>
              <Sparkle size={26} color="#F9F6F0" weight="fill" />
            </div>
            <div>
              <div className="font-heading text-2xl">Daily Quick Quiz</div>
              <div className="text-sm text-[#5C564D]">5 mixed-topic questions • ~2 minutes</div>
            </div>
          </div>
          <button data-testid="btn-start-quiz" disabled={busy} onClick={start} className="samaaj-btn-primary mt-6 flex items-center gap-2">
            {busy ? "Loading…" : (<>Start quiz <ArrowRight size={16} weight="bold" /></>)}
          </button>
        </div>
      )}

      {quiz && !result && (
        <div className="mt-8 space-y-5" data-testid="quiz-questions">
          {quiz.questions.map((q, qi) => (
            <div key={qi} className="samaaj-card p-6">
              <div className="text-xs font-bold text-[#D49A36]">Question {qi + 1}</div>
              <div className="font-heading text-xl mt-1 text-[#1C1B1A]">{q.q}</div>
              <div className="mt-3 grid gap-2">
                {q.opts.map((o, oi) => {
                  const chosen = answers[qi] === oi;
                  return (
                    <button
                      key={oi}
                      data-testid={`q${qi}-opt-${oi}`}
                      onClick={() => setAnswers((prev) => prev.map((v, i) => (i === qi ? oi : v)))}
                      className="text-left px-4 py-3 rounded-xl border font-medium"
                      style={{
                        background: chosen ? "#2C4A3B" : "#F9F6F0",
                        color: chosen ? "#F9F6F0" : "#1C1B1A",
                        borderColor: chosen ? "#2C4A3B" : "#E5D9CC",
                        transition: "background-color 180ms ease-out, color 180ms ease-out",
                      }}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <button data-testid="btn-submit-quiz" disabled={busy} onClick={submit} className="samaaj-btn-primary w-full">
            {busy ? "Scoring…" : "Submit answers"}
          </button>
        </div>
      )}

      {result && (
        <div className="samaaj-card p-8 mt-8 text-center" data-testid="quiz-result">
          <Trophy size={48} weight="fill" className="mx-auto text-[#D49A36]" />
          <div className="font-heading text-4xl mt-3">{result.correct} / {result.total} correct!</div>
          <div className="mt-2 flex items-center justify-center gap-1 text-lg font-bold text-[#D49A36]"><Coin size={20} weight="fill" /> +{result.reward} credits</div>
          <div className="text-sm text-[#5C564D] mt-2">New balance: <span className="font-bold text-[#1C1B1A]">{result.new_balance} credits</span></div>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button data-testid="btn-play-again" onClick={start} className="samaaj-btn-primary">Play again</button>
            <Link to="/marketplace" data-testid="go-marketplace" className="samaaj-btn-outline">Spend in Marketplace</Link>
          </div>
        </div>
      )}
    </div>
  );
}
